import React, { useRef, useState } from 'react';
import { bool, func } from 'prop-types';
import {
  Typography, DrawerHeader, DrawerTitle, IconButton,
  Menu, MenuItem, Modal, ModalHeader, ModalBody, ModalFooter, Button, ButtonGroup,
} from '@one-thd/sui-atomic-components';
import { Close, More, Refresh, Cancel } from '@one-thd/sui-icons';
import { AssistantIcon } from '../shared/assets/AssistantIcon';
import { AssociateIcon } from '../shared/common/AssociateIcon';
import { emit } from '../platform/events/lifecycle';
import { useAssistant, useAssistantStore } from '../orchestrator/AssistantOrchestrator';

// AssistantShell's DEFAULT header slot (was shared/header/AssistantHeader; moved and renamed
// into shell/ because composing the header is the Shell's job). The orchestrator-coupled
// menu/escalation/close-conversation markup. Which header actually renders — this one, or a
// journey's own (today: `legacy`, see journeys/legacy/journey.js and
// journeys/legacy/README.md) — is decided one level up, by AssistantShell.jsx's `Header`
// helper; this component has no journey awareness of its own.
export const ShellHeader = ({ onClose, isMobile = false }) => {
  // Escalation is read from the one WS-3 source (the conversation slice) — the same
  // store the drawer body selects (DrawerContent). This retires the header/body
  // dual-path where the header took a drilled `isEscalated={session?.isEscalated}`
  // prop while the body read the store.
  const isEscalated = useAssistantStore((state) => state.isEscalated);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const menuAnchorRef = useRef(null);

  // Direct call to the conversation's owner (Seq D.4). This used to open a throwaway
  // BroadcastChannel and post MAA_RESET so the orchestrator — the header's own ANCESTOR —
  // would hear it. The orchestrator still notifies peer tabs; that hop is its to own.
  const { resetConversation } = useAssistant();
  const onReset = () => {
    resetConversation();
    setMenuOpen(false);
  };

  const handleMenuToggle = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const handleCloseConversationClick = () => {
    setMenuOpen(false);
    setConfirmModalOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmModalOpen(false);
    emit('magic-apron-assistant.close-agent-conversation');
  };

  const handleReturnToConversation = () => {
    setConfirmModalOpen(false);
  };

  return (
    <>
      <DrawerHeader
        endAdornment={(
          <div className="sui-flex sui-items-center sui-gap-4">
            <IconButton
              ref={menuAnchorRef}
              aria-label="overflow menu"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              onClick={handleMenuToggle}
              icon={More}
            />
            <Menu
              anchorEl={menuAnchorRef.current}
              open={menuOpen}
              onClose={handleMenuClose}
              placement="bottom-end"
            >
              {isEscalated ? (
                <MenuItem onClick={handleCloseConversationClick}>
                  <Cancel />
                  <span className="sui-pl-2">Close conversation</span>
                </MenuItem>
              ) : (
                <MenuItem onClick={onReset}>
                  <Refresh />
                  <span className="sui-pl-2">Start a new chat</span>
                </MenuItem>
              )}
            </Menu>
            <IconButton
              aria-label="close"
              onClick={onClose}
              icon={Close}
            />
          </div>
        )}
        title={(
          <DrawerTitle
            disableGutters
            ornament={isEscalated
              ? <AssociateIcon size="small" spacing="8" />
              : <AssistantIcon height="24px" width="24px" />}
          >
            {isEscalated ? (
              <span>Associate</span>
            ) : (
              <div className="sui-flex sui-justify-center sui-align-center">
                <span className="sui-text-xl">Magic Apron </span>
                <Typography variant="body-xs" color="medium" component="span">
                  <span className="sui-align-super sui-pl-1">Beta</span>
                </Typography>
              </div>
            )}
          </DrawerTitle>
        )}
        className="drawer-header-div sui-flex-col sui-sticky sui-px-2 sui-pt-2 sui-pb-3 sui-bg-primary sui-z-200"
        data-component="AssistantHeader"
      />
      <Modal
        open={confirmModalOpen}
        onClose={handleReturnToConversation}
        aria-labelledby="close-conversation-title"
        aria-describedby="close-conversation-description"
      >
        <ModalHeader id="close-conversation-title" onClose={handleReturnToConversation}>
          Close conversation
        </ModalHeader>
        <ModalBody id="close-conversation-description">
          Closing will end the conversation with a live associate.
        </ModalBody>
        <ModalFooter position="end">
          <ButtonGroup>
            {isMobile ? (
              [
                <Button key="primary" variant="primary" onClick={handleConfirmClose}>
                  Close
                </Button>,
                <Button key="secondary" variant="secondary" onClick={handleReturnToConversation}>
                  Return to Conversation
                </Button>,
              ]
            ) : (
              [
                <Button key="secondary" variant="secondary" onClick={handleReturnToConversation}>
                  Return to Conversation
                </Button>,
                <Button key="primary" variant="primary" onClick={handleConfirmClose}>
                  Close
                </Button>,
              ]
            )}
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </>
  );
};

ShellHeader.propTypes = {
  onClose: func.isRequired,
  isMobile: bool,
};
